import { cubicBezier } from "@motionone/easing";
import { lerp, map, clamp01 } from "./math/index.js";
import { isDefined, isArray } from "./is.js";
import { noop, noopValue as linear } from "./noop.js";
import useRaf from "../services/raf.js";
let id = 0;
const DEFAULT_PROGRESS_PRECISION = 1e-4;
function normalizeEase(ease) {
  if (!isDefined(ease)) {
    return linear;
  }
  if (isArray(ease)) {
    return cubicBezier(...ease);
  }
  return ease;
}
function tween(callback, options = {}) {
  const raf = useRaf();
  let progressValue = 0;
  let easedProgress = 0;
  const precision = options.precision ?? DEFAULT_PROGRESS_PRECISION;
  const ease = normalizeEase(options.easing);
  let duration = options.duration ?? 1;
  duration *= 1e3;
  let startTime = performance.now();
  let endTime = startTime + duration;
  const key = `tw-${id}`;
  id += 1;
  const { onStart = noop, onProgress = noop, onFinish = noop } = options;
  let isRunning = false;
  function pause() {
    isRunning = false;
    raf.remove(key);
  }
  function progress(newProgress) {
    if (typeof newProgress === "undefined") {
      return easedProgress;
    }
    progressValue = newProgress;
    easedProgress = ease(progressValue);
    if (Math.abs(1 - easedProgress) < precision) {
      progressValue = 1;
      easedProgress = 1;
    }
    callback(easedProgress);
    onProgress(progressValue, easedProgress);
    if (easedProgress === 1) {
      pause();
      requestAnimationFrame(() => onFinish(progressValue, easedProgress));
    }
    return progressValue;
  }
  function tick(props) {
    if (!isRunning) {
      raf.remove(key);
      return;
    }
    progress(clamp01(map(props.time, startTime, endTime, 0, 1)));
  }
  function start() {
    onStart();
    startTime = performance.now();
    endTime = startTime + duration;
    progressValue = 0;
    easedProgress = 0;
    isRunning = true;
    raf.add(key, tick);
  }
  function play() {
    if (isRunning) {
      return;
    }
    startTime = performance.now() - lerp(0, duration, progressValue);
    endTime = startTime + duration;
    isRunning = true;
    raf.add(key, tick);
  }
  return {
    start,
    finish: () => progress(1),
    pause,
    play,
    progress
  };
}
export {
  normalizeEase,
  tween
};
//# sourceMappingURL=tween.js.map
